import { tokens, Loading } from '@beeline.com/bp-design-system';
import { useMixpanel } from 'integrations/mixpanel/useMixpanel';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import {
  HelpDocsOpenInNewTabDetails,
  HelpDocsPageViewDetails,
  HelpDocsToggleDetails
} from 'src/integrations/mixpanel/Events/helpDocs';
import styled from 'styled-components';
import DocsDrawerFooter from './DocsDrawerFooter';
import { DocsMenuBar } from './DocsMenuBar';
import { ResizeDrawer } from './ResizeDrawer';
import { useDocsSidePanelIntl } from './translations/useDocsSidePanelIntl';

interface Props {
  onClose: () => void;
  isOpen: boolean;
}

export const HELP_DOCS_I_FRAME_ID = 'helpDocsIFrameId';
const DOCS_PAGE_ORIGIN = 'https://docs.bpe.beeline.com';

export const DocsDrawer: React.FC<React.PropsWithChildren<Props>> = ({ onClose, isOpen }) => {
  const intl = useDocsSidePanelIntl();
  const { route, track } = useMixpanel();
  const [currentHelpPage, setCurrentHelpPage] = React.useState(`${DOCS_PAGE_ORIGIN}/beeline-professional/index.html`);
  const [loading, setLoading] = useState(true);
  const routeRef = useRef(route);
  const currentHelpPageRef = useRef(route);

  useEffect(() => {
    routeRef.current = route;
  }, [route]);

  useEffect(() => {
    currentHelpPageRef.current = currentHelpPage;
  }, [currentHelpPage]);

  const handleIFrameLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  const handleNavToNewPage = (newHelpPage: string) => {
    if (currentHelpPage !== newHelpPage) {
      track<HelpDocsPageViewDetails>(MixpanelEvents.helpDocs.PAGE_VIEW, {
        from: currentHelpPage,
        to: newHelpPage,
        appPage: route
      });
      setCurrentHelpPage(newHelpPage);
    }
  };

  const handleOpenInNewTab = () => {
    if (currentHelpPage) {
      track<HelpDocsOpenInNewTabDetails>(MixpanelEvents.helpDocs.OPEN_IN_NEW_TAB, {
        page: currentHelpPage,
        appPage: route
      });
      window.open(currentHelpPage, '_blank')?.focus();
    }
  };

  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpenRef.current !== isOpen) {
      track<HelpDocsToggleDetails>(MixpanelEvents.helpDocs.TOGGLE, {
        toggleAction: isOpen ? 'open' : 'close',
        page: currentHelpPageRef.current,
        appPage: routeRef.current
      });
      isOpenRef.current = isOpen;
    }
  }, [isOpen, track]);

  return (
    isOpen && (
      <ResizeDrawer data-test="help-docs-drawer">
        <DocsMenuBar
          onClose={onClose}
          docsPageOrigin={DOCS_PAGE_ORIGIN}
          currentPage={currentHelpPage}
          onNavToNewPage={handleNavToNewPage}
          onOpenInNewTab={handleOpenInNewTab}
        />
        {loading && (
          <StyledLoader>
            <Loading size="medium" />
          </StyledLoader>
        )}
        <StyledContainer data-test={'help-docs-iframe'}>
          <StyledIframe
            $show={!loading}
            id={HELP_DOCS_I_FRAME_ID}
            onLoad={handleIFrameLoaded}
            src={currentHelpPage}
            title={intl.panelTitle}
          />
        </StyledContainer>
        <DocsDrawerFooter />
      </ResizeDrawer>
    )
  );
};

interface ContainerProps {
  $show: boolean;
}

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  border-left: 1px solid ${tokens.colorSurfaceVariant};
`;

const StyledIframe = styled.iframe<ContainerProps>`
  display: ${({ $show }) => ($show ? 'block' : 'none')};
  width: 100%;
  height: 100%;
  border: none;
`;

const StyledLoader = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;
`;
